import React, { ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Row, Col } from '@evertel/web/ui'
import { UserTile } from '@evertel/web/user'
import { ACLRoomTile } from './ACLRoomTile'
import { DocumentACLsController } from '@evertel/document'
import { ACLButtonProps, ACLButton } from './ACLButton'

type ACLProps = ACLButtonProps & {
    itemState?: 'normal'|'new'|'deleted'|'modified'
    /** Rendered after the room name, e.g. the external-connectors warning */
    trailingIcon?: ReactNode
}

export const ACL: React.FC<ACLProps> = observer(({
    attachId,
    acl,
    documentACLController,
    onChangeACL,
    isNew,
    busy,
    itemState = 'normal',
    trailingIcon
}) => {

    if (!acl) return null

    // handles missing acl but also filters out current department ACL which isnt used yet
    if (!isNew && !acl.id) return null

    // staged rooms have no server-side ACL to check, and the owner row is never actionable
    const isOwner = acl.accessType === DocumentACLsController.accessTypes.OWNER
    const canEditACL = isNew || isOwner || documentACLController.canEditACL(acl.id)

    const renderTile = () => {
        if (acl.principalType === DocumentACLsController.principalTypes.Room) {
            // ROOM
            return (
                <ACLRoomTile
                    roomId={acl.principalId}
                    showMembersCount={true}
                    trailingIcon={trailingIcon}
                />
            )
        } else if (acl.principalType === DocumentACLsController.principalTypes.BlueUser) {
            // USER
            return (
                <UserTile
                    userId={acl.principalId}
                    imageWidth={25}
                />
            )
        } else {
            return null
        }
    }

    return (
        <Row
            className={classNames('my-2 px-2 py-1 rounded-2', {
                'bg-info-subtle': itemState === 'new' || itemState === 'modified',
                'bg-danger-subtle': itemState === 'deleted'
            })}
            // matches mobile: rows the user can't act on are dimmed rather than hidden
            style={{ opacity: canEditACL ? undefined : 0.6 }}
            valign="center">
            <Col xs={8}>
                {renderTile()}
            </Col>
            <Col
                xs={4}
                align="right">
                <ACLButton
                    attachId={attachId}
                    acl={acl}
                    documentACLController={documentACLController}
                    onChangeACL={onChangeACL}
                    isNew={isNew}
                    busy={busy}
                    disabled={!canEditACL}
                />
            </Col>
        </Row>
    )
})
